/**
 * @fileoverview WEB-1079 — the Discover day strip's chip model (v3.1
 * `.dv-days` / `.dv-dayc`, DS 1.9.0 `templates/discover.html`).
 *
 * Pure and isomorphic: given the active date window, the city timezone, the
 * day sections currently in the DOM, and `facets.calendar`, it returns one
 * chip per **city-local** calendar day in the window.
 *
 * ## Spans mirror the API, not the preset label
 *
 * Each preset resolves through `resolveDateWindowKeyword` from
 * `@loci/validation` — the same function `/events/discover` measures with
 * (API-1820) — and the strip spans the city-local dates of that window, so a
 * chip never claims a day the list cannot contain:
 *
 * | URL `date`        | API keyword        | Span                                      |
 * | ----------------- | ------------------ | ----------------------------------------- |
 * | _(none)_          | _(unbounded)_      | today … today + 6 (the mock's 7 days)     |
 * | `tonight`         | `today`            | today (one chip; the window ends 5 AM)    |
 * | `tomorrow`        | `tomorrow`         | today + 1                                 |
 * | `weekend`         | `this-weekend`     | Fri … Sun (from today once it has begun)  |
 * | `next7`           | `next7`            | today … city-local date of now+168h       |
 * | `next30`          | `next30`           | today … today + 30 calendar days          |
 * | `this-week`       | `this-week`        | today … Sunday (city-local)               |
 * | `YYYY-MM-DD..…`   | same               | start … end                               |
 *
 * A span never starts before city-local today: the list's not-over floor
 * drops ended events, so an earlier day can only hold in-progress events,
 * which surface through the single leading `on-now` chip instead (DQ-5).
 *
 * ## Dates are calendar math, never wall-clock math (WEB-924)
 *
 * "Today" is `formatEventTime(now, tz).localDate`, and every other day is
 * that `YYYY-MM-DD` plus N calendar days on a UTC anchor. Weekday and day
 * number derive from the calendar date itself, so "Tomorrow" can never print
 * today's weekday, and a DST transition (23 h or 25 h day) can neither skip
 * nor repeat a day.
 */
import { formatEventTime } from '@loci/types';
import {
  addLocalDays,
  dateWindowPreset,
  isDateWindowPreset,
  isLocalDateKey,
  localDateKey,
  localWeekday,
  resolveDateWindowKeyword,
} from '@loci/validation';

import { readDiscoverRequestedWindow } from './requestedWindow';
import type { DateValue } from './url-state';

/** `loaded` = a day section is in the DOM; `far` = in range, not loaded yet;
 *  `empty` = no events that day (`facets.calendar` has no key); `on-now` = the
 *  single leading chip for in-progress sections dated before the range. */
export type DayStripChipState = 'loaded' | 'far' | 'empty' | 'on-now';

/** `tonight` renders one chip with a ` · tonight` suffix and no tail link. */
export type DayStripScope = 'tonight' | 'range';

export interface DayStripRange {
  /** First city-local day, `YYYY-MM-DD`, inclusive. */
  readonly start: string;
  /** Last city-local day, `YYYY-MM-DD`, inclusive. */
  readonly end: string;
}

export interface DayStripChip {
  /** City-local `YYYY-MM-DD`; the target section is `#discover-day-{localDate}`. */
  readonly localDate: string;
  /** Three-letter weekday, sentence case (`Tue`); CSS uppercases it. */
  readonly weekday: string;
  /** Day of month, unpadded (`22`). */
  readonly dayNumber: number;
  readonly state: DayStripChipState;
}

export interface BuildDayStripChipsInput {
  readonly range: DayStripRange;
  /** IANA zone — accepted for API symmetry with the span resolver. */
  readonly cityTimezone: string;
  /** `YYYY-MM-DD` of every `section[data-local-date]` currently rendered. */
  readonly loadedDates: readonly string[];
  /**
   * `facets.calendar` (`YYYY-MM-DD → count`; the backend emits only days with
   * at least one event). `undefined` when the envelope has not landed or the
   * calendar facet is degraded — then no day can be called `empty`, and an
   * unloaded day stays `far`.
   */
  readonly calendarCounts: Readonly<Record<string, number>> | undefined;
  readonly scope: DayStripScope;
}

export interface DayStripWindow {
  readonly range: DayStripRange;
  readonly scope: DayStripScope;
  /** Whether the trailing `+ 30 days →` link renders (DQ-6). */
  readonly showMoreLink: boolean;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
const LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;
const ISO_RANGE = /^(\d{4}-\d{2}-\d{2})\.\.(\d{4}-\d{2}-\d{2})$/;
/** The mock's upcoming strip: seven days, then `+ 30 days →`. */
const UPCOMING_SPAN_DAYS = 7;
/** Guard against a pathological custom range rendering thousands of buttons. */
const MAX_SPAN_DAYS = 62;

export { addLocalDays };

/** City-local today, the WEB-924 way. */
export function cityLocalToday(now: Date, cityTimezone: string): string {
  return formatEventTime(now, cityTimezone).localDate;
}

/**
 * The strip's span for a URL `date` value — the window the API measured (see
 * the table in the file header), clamped to start no earlier than today.
 * Returns `null` for a value the strip cannot place (malformed custom range).
 */
export function resolveDayStripWindow(
  date: DateValue | undefined,
  now: Date,
  cityTimezone: string,
): DayStripWindow | null {
  const today = cityLocalToday(now, cityTimezone);
  const span = (start: string, end: string, scope: DayStripScope = 'range'): DayStripWindow => ({
    range: { start: start < today ? today : start, end },
    scope,
    // DQ-6: `+ 30 days` widens to next30 — pointless on tonight, a self-link
    // on next30 itself.
    showMoreLink: scope !== 'tonight' && date !== 'next30',
  });

  if (date === undefined) return span(today, addLocalDays(today, UPCOMING_SPAN_DAYS - 1));
  if (isDateWindowPreset(date)) {
    const window = resolveDateWindowKeyword(dateWindowPreset(date).keyword, cityTimezone, now);
    const start = localDateKey(window.start, cityTimezone);
    // `tonight` is one chip even though its window runs to 5 AM tomorrow.
    if (date === 'tonight') return span(start, start, 'tonight');
    return span(start, localDateKey(window.end, cityTimezone));
  }
  const match = ISO_RANGE.exec(date);
  if (!match || !isLocalDateKey(match[1]!) || !isLocalDateKey(match[2]!)) return null;
  return span(match[1]!, match[2]!);
}

/**
 * Prefer `meta.requestedWindow` when the response carries a valid one, so the
 * strip matches the window the API measured. A missing or malformed field
 * falls back to {@link resolveDayStripWindow} (the same contract, computed
 * locally). `servedWindow` is not consulted.
 */
export function resolveDayStripWindowForResponse(
  date: DateValue | undefined,
  now: Date,
  cityTimezone: string,
  meta: unknown,
): DayStripWindow | null {
  const requested = readDiscoverRequestedWindow(meta);
  if (requested) {
    const startMs = Date.parse(requested.start);
    const endMs = Date.parse(requested.end);
    const today = cityLocalToday(now, cityTimezone);
    const start = formatEventTime(new Date(startMs), cityTimezone).localDate;
    const end = formatEventTime(new Date(endMs), cityTimezone).localDate;
    if (end >= start && end >= today) {
      const scope: DayStripScope = date === 'tonight' ? 'tonight' : 'range';
      return {
        range: { start: start < today ? today : start, end },
        scope,
        showMoreLink: scope !== 'tonight' && date !== 'next30',
      };
    }
  }
  return resolveDayStripWindow(date, now, cityTimezone);
}

function chipFor(localDate: string, state: DayStripChipState): DayStripChip {
  return {
    localDate,
    weekday: WEEKDAYS[localWeekday(localDate)]!,
    dayNumber: isLocalDateKey(localDate) ? Number(localDate.slice(8, 10)) : 0,
    state,
  };
}

/**
 * One chip per city-local day in `range` (one, today, under `tonight`) — and
 * on through the last loaded day if the list has run past `range.end` — plus
 * at most one leading `on-now` chip when a loaded section is dated before the
 * range start (DQ-5); it targets the earliest such section.
 *
 * Per day: a section in the DOM wins (`loaded`); otherwise a day with no
 * `facets.calendar` key is `empty` (DQ-3); otherwise it is `far`.
 */
export function buildDayStripChips({
  range,
  loadedDates,
  calendarCounts,
  scope,
}: BuildDayStripChipsInput): DayStripChip[] {
  const loaded = new Set(loadedDates);
  const chips: DayStripChip[] = [];

  const beforeRange = loadedDates.filter((d) => LOCAL_DATE.test(d) && d < range.start).sort();
  if (beforeRange.length > 0) chips.push(chipFor(beforeRange[0]!, 'on-now'));

  // A list that runs past the window (upcoming is unbounded; the strip shows
  // seven days) still gets a chip for every loaded day, so the spy always has
  // a chip to mark current. Bounded presets never load days past `end`.
  const lastLoaded = loadedDates
    .filter((d) => LOCAL_DATE.test(d))
    .sort()
    .at(-1);
  const end =
    scope === 'tonight'
      ? range.start
      : lastLoaded !== undefined && lastLoaded > range.end
        ? lastLoaded
        : range.end;
  let day = range.start;
  for (let i = 0; i < MAX_SPAN_DAYS && day <= end; i += 1) {
    const state: DayStripChipState = loaded.has(day)
      ? 'loaded'
      : calendarCounts !== undefined && !(calendarCounts[day]! > 0)
        ? 'empty'
        : 'far';
    chips.push(chipFor(day, state));
    day = addLocalDays(day, 1);
  }
  return chips;
}
